import React, { useState, useEffect } from "react";
import "./UsageLimit.css";
import Sidebar from "../../../Navigation/Sidebar/Sidebar";

const UsageLimitComponent = (props) => {
  const {
    loading,
    usageLimitData,
    isEditMode,
    formData,
    handleBackClick,
    handleEditClick,
    handleCancelClick,
    handleInputChange,
    handleUpdateLimit,
    corporateId
  } = props;

  // Local state to track UI changes immediately
  const [localUsageLimitRequired, setLocalUsageLimitRequired] = useState(formData.usageLimitRequired);
  const [localLimitOn, setLocalLimitOn] = useState(formData.limitOn);
  const [localFrequency, setLocalFrequency] = useState(formData.frequency);
  const [localValue, setLocalValue] = useState(formData.value);

  // Sync local state with formData when props change
  useEffect(() => {
    setLocalUsageLimitRequired(formData.usageLimitRequired);
    setLocalLimitOn(formData.limitOn);
    setLocalFrequency(formData.frequency);
    setLocalValue(formData.value);
  }, [formData.usageLimitRequired, formData.limitOn, formData.frequency, formData.value]);

  if (loading) {
    return (
      <div className="usage-limit__container">
        <div className="usage-limit__sidebar">
          <Sidebar />
        </div>
        <div className="usage-limit__main">
          <div className="usage-limit__header">
            <div className="usage-limit__header-left">
              <button className="usage-limit__back-btn" onClick={handleBackClick}>
                ←
              </button>
              <h2 className="usage-limit__title">Usage Limit</h2>
            </div>
          </div>
          <div className="usage-limit__loading">
            <div className="usage-limit__spinner"></div>
            <p className="usage-limit__loading-text">Loading usage limit data...</p>
          </div>
        </div>
      </div>
    );
  }

  if (!usageLimitData) {
    return (
      <div className="usage-limit__container">
        <div className="usage-limit__sidebar">
          <Sidebar />
        </div>
        <div className="usage-limit__main">
          <div className="usage-limit__header">
            <div className="usage-limit__header-left">
              <button className="usage-limit__back-btn" onClick={handleBackClick}>
                ←
              </button>
              <h2 className="usage-limit__title">Usage Limit</h2>
            </div>
          </div>
          <div className="usage-limit__error">
            <div className="usage-limit__error-icon">⚠️</div>
            <h3 className="usage-limit__error-heading">No Data Found</h3>
            <p className="usage-limit__error-message">Unable to load usage limit data for this corporate.</p>
          </div>
        </div>
      </div>
    );
  }

  // Helper to display frequency in readable format
  const getFrequencyDisplay = (frequency) => {
    if (frequency === "HOURS") return "Hour";
    if (frequency === "DAYS") return "Day";
    if (frequency === "MONTHS") return "Month";
    return frequency;
  };

  // Helper to display limit type in readable format
  const getLimitTypeDisplay = (limitOn) => {
    if (limitOn === "AMOUNT") return "Amount";
    if (limitOn === "TRANSACTION_COUNT") return "Transaction";
    return limitOn;
  };

  // Handle usage limit required change
  const handleUsageLimitRequiredChange = (value) => {
    const isRequired = value === "true";
    
    // Update local state immediately for UI
    setLocalUsageLimitRequired(isRequired);
    
    // Update parent state
    handleInputChange("usageLimitRequired", isRequired);
    
    // If user selects "No", clear other fields
    if (!isRequired) {
      setLocalLimitOn("");
      setLocalFrequency("");
      setLocalValue("");
      handleInputChange("limitOn", "");
      handleInputChange("frequency", "");
      handleInputChange("value", "");
    }
  };

  // Handle limit on change
  const handleLimitOnChange = (value) => {
    setLocalLimitOn(value);
    setLocalValue("");
    handleInputChange("limitOn", value);
    handleInputChange("value", "");
  };

  // Handle frequency change
  const handleFrequencyChange = (value) => {
    setLocalFrequency(value);
    setLocalValue("");
    handleInputChange("frequency", value);
    handleInputChange("value", "");
  };

  // Handle input change with validation for numbers only and max 6 digits
  const handleNumberInputChange = (e) => {
    const value = e.target.value;
    
    // Allow only numbers (positive integers)
    if (value === "" || /^\d+$/.test(value)) {
      // Check if the number has more than 6 digits
      if (value.length > 6) {
        return; // Don't allow more than 6 digits
      }
      setLocalValue(value);
      handleInputChange("value", value);
    }
  };

  // Prevent wheel events on the input field
  const handleWheel = (e) => {
    e.preventDefault();
    e.target.blur();
  };

  // Prevent keydown for arrow keys
  const handleKeyDown = (e) => {
    if (e.key === "ArrowUp" || e.key === "ArrowDown") {
      e.preventDefault();
    }
  };

  // Check if usage limit is required (use local state for immediate UI update)
  const isUsageLimitRequired = localUsageLimitRequired === true;

  // Check if value input should be enabled
  const isValueEnabled = isUsageLimitRequired && localLimitOn && localFrequency;

  return (
    <div className="usage-limit__container">
      <div className="usage-limit__sidebar">
        <Sidebar />
      </div>

      <div className="usage-limit__main">
        <div className="usage-limit__header">
          <div className="usage-limit__header-left">
            <button className="usage-limit__back-btn" onClick={handleBackClick}>
              ←
            </button>
            <h2 className="usage-limit__title">Usage Limit</h2>
            <span className="usage-limit__corp-id">Corporate ID: {corporateId}</span>
          </div>
          
          <div className="usage-limit__header-right">
            {isEditMode && (
              <div className="usage-limit__edit-actions">
                <button className="usage-limit__cancel-btn" onClick={handleCancelClick}>
                  Cancel
                </button>
                <button className="usage-limit__save-btn" onClick={handleUpdateLimit}>
                  Save Changes
                </button>
              </div>
            )}
          </div>
        </div>

        <div className="usage-limit__content">
          <div className="usage-limit__page-header">
            <h1 className="usage-limit__page-title">Usage Limit</h1>
            <p className="usage-limit__page-subtitle">Define spending thresholds and transaction volume controls for this corporate account.</p>
          </div>

          <div className="usage-limit__card">
            <div className="usage-limit__card-header">
              <div className="usage-limit__corp-info">
                <div className="usage-limit__info-label">CORPORATE</div>
                <div className="usage-limit__corp-name">{`Corporate ${corporateId}`}</div>
              </div>
            </div>

            <div className="usage-limit__card-body">
              <div className="usage-limit__grid">
                {/* Usage Limit Required */}
                <div className="usage-limit__grid-item">
                  <div className="usage-limit__field-label">USAGE LIMIT REQUIRED</div>
                  {isEditMode ? (
                    <div className="usage-limit__radio-group">
                      <label className="usage-limit__radio-label">
                        <input
                          type="radio"
                          name="usageLimitRequired"
                          value="true"
                          checked={localUsageLimitRequired === true}
                          onChange={(e) => handleUsageLimitRequiredChange(e.target.value)}
                          className="usage-limit__radio-input"
                        />
                        <span className="usage-limit__radio-custom"></span>
                        <span className="usage-limit__radio-text">Yes</span>
                      </label>
                      <label className="usage-limit__radio-label">
                        <input
                          type="radio"
                          name="usageLimitRequired"
                          value="false"
                          checked={localUsageLimitRequired === false}
                          onChange={(e) => handleUsageLimitRequiredChange(e.target.value)}
                          className="usage-limit__radio-input"
                        />
                        <span className="usage-limit__radio-custom"></span>
                        <span className="usage-limit__radio-text">No</span>
                      </label>
                    </div>
                  ) : (
                    <div className={`usage-limit__status-badge ${usageLimitData.usageLimitRequired ? "usage-limit__status-yes" : "usage-limit__status-no"}`}>
                      {usageLimitData.usageLimitRequired ? "Yes" : "No"}
                    </div>
                  )}
                </div>

                {/* These fields only show when usage limit is required */}
                {isUsageLimitRequired && (
                  <>
                    {/* What do you want to limit? */}
                    <div className="usage-limit__grid-item">
                      <div className="usage-limit__field-label">WHAT DO YOU WANT TO LIMIT?</div>
                      {isEditMode ? (
                        <div className="usage-limit__radio-group">
                          <label className="usage-limit__radio-label">
                            <input
                              type="radio"
                              name="limitOn"
                              value="AMOUNT"
                              checked={localLimitOn === "AMOUNT"}
                              onChange={(e) => handleLimitOnChange(e.target.value)}
                              className="usage-limit__radio-input"
                            />
                            <span className="usage-limit__radio-custom"></span>
                            <span className="usage-limit__radio-text">Amount</span>
                          </label>
                          <label className="usage-limit__radio-label">
                            <input
                              type="radio"
                              name="limitOn"
                              value="TRANSACTION_COUNT"
                              checked={localLimitOn === "TRANSACTION_COUNT"}
                              onChange={(e) => handleLimitOnChange(e.target.value)}
                              className="usage-limit__radio-input"
                            />
                            <span className="usage-limit__radio-custom"></span>
                            <span className="usage-limit__radio-text">Transaction</span>
                          </label>
                        </div>
                      ) : (
                        <div className="usage-limit__type-badge">
                          {usageLimitData.limitOn ? getLimitTypeDisplay(usageLimitData.limitOn) : "N/A"}
                        </div>
                      )}
                    </div>

                    {/* Frequency */}
                    <div className="usage-limit__grid-item">
                      <div className="usage-limit__field-label">FREQUENCY</div>
                      {isEditMode ? (
                        <div className="usage-limit__radio-group">
                          <label className="usage-limit__radio-label">
                            <input
                              type="radio"
                              name="frequency"
                              value="HOURS"
                              checked={localFrequency === "HOURS"}
                              onChange={(e) => handleFrequencyChange(e.target.value)}
                              className="usage-limit__radio-input"
                            />
                            <span className="usage-limit__radio-custom"></span>
                            <span className="usage-limit__radio-text">Hour</span>
                          </label>
                          <label className="usage-limit__radio-label">
                            <input
                              type="radio"
                              name="frequency"
                              value="DAYS"
                              checked={localFrequency === "DAYS"}
                              onChange={(e) => handleFrequencyChange(e.target.value)}
                              className="usage-limit__radio-input"
                            />
                            <span className="usage-limit__radio-custom"></span>
                            <span className="usage-limit__radio-text">Day</span>
                          </label>
                          <label className="usage-limit__radio-label">
                            <input
                              type="radio"
                              name="frequency"
                              value="MONTHS"
                              checked={localFrequency === "MONTHS"}
                              onChange={(e) => handleFrequencyChange(e.target.value)}
                              className="usage-limit__radio-input"
                            />
                            <span className="usage-limit__radio-custom"></span>
                            <span className="usage-limit__radio-text">Month</span>
                          </label>
                        </div>
                      ) : (
                        <div className="usage-limit__frequency-badge">
                          {usageLimitData.frequency ? getFrequencyDisplay(usageLimitData.frequency) : "N/A"}
                        </div>
                      )}
                    </div>

                    {/* Set Limit Value */}
                    <div className="usage-limit__grid-item">
                      <div className="usage-limit__field-label">SET LIMIT VALUE</div>
                      {isEditMode ? (
                        <div className="usage-limit__input-wrapper">
                          <span className="usage-limit__currency">
                            {localLimitOn === "AMOUNT" ? "₹" : ""}
                          </span>
                          <input
                            type="text"
                            inputMode="numeric"
                            value={localValue || ""}
                            onChange={handleNumberInputChange}
                            onWheel={handleWheel}
                            onKeyDown={handleKeyDown}
                            className="usage-limit__input-field"
                            placeholder={
                              localLimitOn === "AMOUNT" 
                                ? "Enter amount (max 6 digits)" 
                                : "Enter number of transactions (max 6 digits)"
                            }
                            disabled={!isValueEnabled}
                            maxLength="6"
                          />
                          <span className="usage-limit__max-hint">(Max 6 digits)</span>
                        </div>
                      ) : (
                        <div className="usage-limit__limit-value">
                          {usageLimitData.value || "N/A"}
                        </div>
                      )}
                    </div>
                  </>
                )}
              </div>

              {!isEditMode && (
                <div className="usage-limit__action-section">
                  <button className="usage-limit__update-btn" onClick={handleEditClick}>
                    Update Limit
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UsageLimitComponent;